import type { CampaignSelectDto } from '@@types/api.types';
import { MainLayout } from '@components/layouts/main.layout';
import { CampaignModal } from '@components/pages/campaigns/components/campaign-modal.component';
import { CampaignStatusBadge } from '@components/shared/campaign-status-badge.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table';
import { useGetCampaigns } from '@queries/campaigns.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { canAccessMeiaCampaigns } from '@utils/meia-access.util';
import { ArrowDownWideNarrowIcon, ArrowUpDown, ArrowUpNarrowWideIcon, FolderPlusIcon, Loader2Icon } from 'lucide-react';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const dateFormatter = new Intl.DateTimeFormat('en-CA');

function getUpdatedDate(value: string): string {
  return dateFormatter.format(new Date(value));
}

function getVignetteCount(campaign: CampaignSelectDto): number {
  return campaign.scenarios.reduce((total, scenario) => total + scenario.vignettes.length, 0);
}

export const CampaignsListPage: FC = () => {
  const { data: user, isPending: isUserPending } = useGetCurrentUser();
  const { data: campaigns = [], error, isError, isLoading } = useGetCampaigns();
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([{ desc: true, id: 'updatedAt' }]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const columns = useMemo<ColumnDef<CampaignSelectDto>[]>(
    () => [
      {
        accessorKey: 'name',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.name}</span>,
        header: 'Name',
        size: 280,
      },
      {
        accessorKey: 'status',
        cell: ({ row }) => <CampaignStatusBadge status={row.original.status} />,
        header: 'Status',
        size: 130,
      },
      {
        accessorFn: (campaign) => campaign.leadCommands.join(', '),
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.leadCommands.length > 0 ? (
              row.original.leadCommands.map((command) => (
                <Badge
                  key={command}
                  size="xs"
                  variant="secondary"
                >
                  {command}
                </Badge>
              ))
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
        ),
        header: 'Lead Cmd',
        id: 'leadCommands',
        size: 260,
      },
      {
        accessorKey: 'timeWindow',
        cell: ({ row }) => (
          <span className="font-mono text-sm text-gray-700">{row.original.timeWindow?.trim() || '-'}</span>
        ),
        header: 'Window',
        size: 130,
      },
      {
        accessorFn: (campaign) => campaign.decisivePoints.length,
        cell: ({ row }) => (
          <span className="font-mono text-sm text-gray-700">{row.original.decisivePoints.length}</span>
        ),
        header: 'DPs',
        id: 'decisivePoints',
        size: 90,
      },
      {
        accessorFn: getVignetteCount,
        cell: ({ row }) => (
          <span className="font-mono text-sm text-gray-700">
            {row.original.scenarios.length}/{getVignetteCount(row.original)}
          </span>
        ),
        header: 'Scn/Vig',
        id: 'scenarios',
        size: 110,
      },
      {
        accessorKey: 'updatedAt',
        cell: ({ row }) => (
          <span className="font-mono text-sm text-gray-600">{getUpdatedDate(row.original.updatedAt)}</span>
        ),
        header: 'Updated',
        size: 140,
      },
    ],
    [],
  );

  const table = useReactTable({
    columns,
    data: campaigns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  if (!isUserPending && !canAccessMeiaCampaigns(user)) {
    return <Navigate to={routes.home.path} />;
  }

  const handleCreateSuccess = (campaignId: string): void => {
    setIsCreateOpen(false);
    navigate(`${routes.campaigns.path}/${campaignId}`);
  };

  return (
    <PageviewTracker>
      <HelmetWrapper title="Campaigns" />
      <MainLayout fullWidth>
        <div className="mx-auto flex w-full max-w-(--breakpoint-lg) flex-col gap-6">
          <Tile
            header={{
              actions: (
                <Button
                  onClick={() => setIsCreateOpen(true)}
                  variant="link"
                >
                  + Add Campaign
                </Button>
              ),
              title: 'Campaigns',
            }}
          >
            <div className="flex flex-col gap-4">
              {isError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error?.errors[0]?.message ?? 'Unable to load campaigns.'}
                </div>
              )}

              {isLoading || isUserPending ? (
                <div className="flex h-48 items-center justify-center rounded-xl border">
                  <Loader2Icon className="size-6 animate-spin text-gray-500" />
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border bg-white">
                  <Table>
                    <TableHeader>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            if (header.isPlaceholder) {
                              return <TableHead key={header.id} />;
                            }

                            const canSort = header.column.getCanSort();
                            const sorted = header.column.getIsSorted();

                            return (
                              <TableHead
                                className="text-xs font-semibold text-gray-500"
                                key={header.id}
                                style={{ width: header.getSize() }}
                              >
                                {canSort ? (
                                  <button
                                    aria-label={`Sort by ${
                                      typeof header.column.columnDef.header === 'string'
                                        ? header.column.columnDef.header
                                        : 'column'
                                    }`}
                                    aria-sort={
                                      sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'
                                    }
                                    className="flex items-center gap-1 text-xs font-semibold text-gray-600"
                                    onClick={header.column.getToggleSortingHandler()}
                                    type="button"
                                  >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {sorted === 'asc' && <ArrowUpNarrowWideIcon className="size-3.5" />}
                                    {sorted === 'desc' && <ArrowDownWideNarrowIcon className="size-3.5" />}
                                    {!sorted && <ArrowUpDown className="size-3.5 text-gray-400" />}
                                  </button>
                                ) : (
                                  flexRender(header.column.columnDef.header, header.getContext())
                                )}
                              </TableHead>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map((row) => (
                          <TableRow
                            className="cursor-pointer hover:bg-gray-50"
                            key={row.id}
                            onClick={() => navigate(`${routes.campaigns.path}/${row.original.id}`)}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell
                                key={cell.id}
                                style={{ width: cell.column.getSize() }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            className="h-40 text-center text-sm text-gray-500"
                            colSpan={columns.length}
                          >
                            <div className="inline-flex items-center gap-2">
                              <FolderPlusIcon className="size-4" />
                              No campaigns found.
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </Tile>
        </div>
      </MainLayout>
      <CampaignModal
        isOpen={isCreateOpen}
        mode="create"
        onClose={() => setIsCreateOpen(false)}
        onCreateSuccess={handleCreateSuccess}
      />
    </PageviewTracker>
  );
};
